/** Phase 17 — Onboarding banner for the Cockpit page.
 *
 * Phase 6 (Feb 2026 fix):
 *   • This component is now DISPLAY-ONLY. The automatic first-login
 *     redirect lives in `routes/app/PostLoginLanding.jsx` — that is
 *     the single source of truth for the `initial_redirect_shown`
 *     flag. Setting the flag here as well used to burn it on plain
 *     `/app/home` visits, which prevented the wizard from ever
 *     auto-firing again for legacy tenants.
 *   • The hero-shaped callout stays so a tenant that hits /app/home
 *     manually still has an obvious path into the setup flow.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { useLang, i18n as ONB_I18N } from "../../i18n/onboarding";

export default function OnboardingBanner() {
  const [state, setState] = useState(null);
  const [lang, _] = useState(useLang());
  const t = ONB_I18N[lang];

  async function load() {
    try {
      const r = await api.get("/onboarding");
      setState(r.data);
    } catch (_e) { /* silent */ }
  }
  useEffect(() => { load(); }, []);   // eslint-disable-line react-hooks/exhaustive-deps

  async function dismiss() {
    try { await api.post("/onboarding/dismiss-banner"); }
    catch (_e) { /* silent */ }
    setState((s) => s ? { ...s, dismissed_banner: true } : s);
  }

  if (!state) return null;
  if (state.completed_at) return null;
  if (state.dismissed_banner) return null;
  const pct = state.progress_pct ?? 0;
  if (pct >= 100) return null;

  // Hero-shaped callout for fresh tenants — the redirect is a
  // one-shot, this is the safety net for tenants that dismissed the
  // redirect via browser back-navigation.
  const fresh = pct === 0;
  return (
    <div
      data-testid="onboarding-banner"
      data-fresh={fresh ? "true" : "false"}
      className={
        fresh
          ? "rounded-lg border-2 border-primary bg-primary/5 p-6"
          : "rounded-lg border bg-slate-50 p-4"
      }
    >
      <div className={fresh
          ? "flex flex-col md:flex-row md:items-center gap-4"
          : "flex items-center gap-4"}>
        <div className="flex-1 space-y-1">
          <div
            data-testid="onboarding-banner-title"
            className={fresh ? "text-xl font-semibold" : "text-sm font-medium"}
          >
            {fresh
              ? (lang === "de"
                  ? "Willkommen — richte deinen Arbeitsbereich ein."
                  : "Welcome — set up your workspace.")
              : `${t.banner_title.replace("{pct}", pct)} · ${t.progress}`}
          </div>
          {fresh && (
            <div
              className="text-sm text-muted-foreground"
              data-testid="onboarding-banner-fresh-copy"
            >
              {lang === "de"
                ? "Firmenprofil, Angebot, Regionen und ein Testbudget in fünf Schritten. Du kannst jederzeit unterbrechen."
                : "Company profile, offering, regions and a test budget in five short steps. Pause any time."}
            </div>
          )}
          {!fresh && state.next_recommended_step && (
            <div className="text-xs text-muted-foreground mt-0.5">
              {t.next_step}: <b>{t.steps[state.next_recommended_step]?.t
                                    || state.next_recommended_step}</b>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button asChild data-testid="onboarding-banner-resume"
                   size={fresh ? "lg" : "default"}>
            <Link to="/app/onboarding">
              {fresh
                ? (lang === "de" ? "Setup jetzt starten" : "Start setup now")
                : t.resume}
            </Link>
          </Button>
          <Button variant="ghost" onClick={dismiss}
                     data-testid="onboarding-banner-dismiss">
            {t.dismiss}
          </Button>
        </div>
      </div>
    </div>
  );
}
